import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { listCopyrightRequests, patchCopyrightRequest } from '../services/copyrightApi';
import { disableAdminFeed, listAdminFeeds } from '../services/feedAdminApi';
import './ResearchPage.css';
import './AdminCopyrightPage.css';

function hostOf(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, '').toLowerCase();
  } catch {
    return '';
  }
}

function matchesFeed(targetUrl, feed) {
  if (!targetUrl || !feed?.url) return false;
  const t = targetUrl.trim().toLowerCase();
  const f = String(feed.url).trim().toLowerCase();
  if (t === f) return true;
  const th = hostOf(targetUrl);
  const fh = hostOf(feed.url);
  return Boolean(th && fh && (th === fh || th.endsWith(`.${fh}`) || fh.endsWith(`.${th}`)));
}

function fmtTime(v) {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return String(v);
  }
}

const AdminCopyrightPage = () => {
  useDocumentTitle('Admin · DMCA');
  const { user, authReady, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('open');
  const [rows, setRows] = useState([]);
  const [feeds, setFeeds] = useState([]);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [notes, setNotes] = useState('');

  const reload = useCallback(async () => {
    const [requests, feedList] = await Promise.all([
      listCopyrightRequests(statusFilter),
      listAdminFeeds().catch(() => []),
    ]);
    setRows(Array.isArray(requests) ? requests : []);
    setFeeds(Array.isArray(feedList) ? feedList : []);
  }, [statusFilter]);

  useEffect(() => {
    if (!authReady) return;
    if (!isAuthenticated || user?.principalName !== 'admin') {
      navigate('/login', { replace: true });
      return;
    }
    reload().catch((e) => setError(e.message));
  }, [authReady, isAuthenticated, user, navigate, reload]);

  const selected = useMemo(
    () => rows.find((r) => r.id === selectedId) || null,
    [rows, selectedId],
  );

  const candidates = useMemo(() => {
    if (!selected?.targetUrl) return [];
    return feeds.filter((f) => matchesFeed(selected.targetUrl, f));
  }, [selected, feeds]);

  const run = async (fn, okMessage) => {
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      await fn();
      setStatus(okMessage);
      await reload();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const patch = (id, payload, msg) => run(() => patchCopyrightRequest(id, payload), msg);

  const disableAndResolve = (request, feed) =>
    run(async () => {
      await disableAdminFeed(feed.id);
      await patchCopyrightRequest(request.id, {
        status: 'resolved',
        resolvedFeedId: feed.id,
        adminNotes: notes || `Disabled feed ${feed.name || feed.url}`,
      });
    }, `Disabled ${feed.name || feed.url} and marked resolved`);

  return (
    <div className="research-page admin-copyright-page">
      <div className="container">
        <header className="admin-copyright-header">
          <h1>DMCA / feed removal</h1>
          <p className="admin-copyright-help">
            Public form at <Link to="/copyright">/copyright</Link>. Disable matching RSS feeds, then
            mark the request resolved. Hard-delete remains on <Link to="/admin/feeds">Config</Link>.
          </p>
        </header>

        <div className="admin-copyright-toolbar">
          <label>
            Status{' '}
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setSelectedId(null);
              }}
            >
              <option value="open">open</option>
              <option value="in_progress">in progress</option>
              <option value="resolved">resolved</option>
              <option value="rejected">rejected</option>
              <option value="all">all</option>
            </select>
          </label>
          {busy && <span>Working…</span>}
          {status && <span className="admin-copyright-ok">{status}</span>}
          {error && <span className="admin-copyright-err">{error}</span>}
        </div>

        <div className="admin-copyright-layout">
          <table className="admin-copyright-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Status</th>
                <th>From</th>
                <th>Target</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={4}>No requests.</td>
                </tr>
              )}
              {rows.map((row) => (
                <tr
                  key={row.id}
                  className={row.id === selectedId ? 'is-selected' : ''}
                  onClick={() => {
                    setSelectedId(row.id);
                    setNotes(row.adminNotes || '');
                  }}
                >
                  <td>{fmtTime(row.createdAt)}</td>
                  <td>{row.status}</td>
                  <td>
                    {row.name}
                    <br />
                    <a href={`mailto:${row.email}`}>{row.email}</a>
                  </td>
                  <td className="admin-copyright-url">{row.targetUrl}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {selected && (
            <aside className="admin-copyright-detail">
              <h2>Request</h2>
              <p>
                <strong>{selected.name}</strong>
                {selected.organization ? ` · ${selected.organization}` : ''}
                <br />
                <a href={`mailto:${selected.email}`}>{selected.email}</a>
              </p>
              <p>
                <strong>Site / feed:</strong>{' '}
                <a href={selected.targetUrl} target="_blank" rel="noopener noreferrer">
                  {selected.targetUrl}
                </a>
              </p>
              {selected.darknewsUrl && (
                <p>
                  <strong>DarkNews URL:</strong>{' '}
                  <a href={selected.darknewsUrl} target="_blank" rel="noopener noreferrer">
                    {selected.darknewsUrl}
                  </a>
                </p>
              )}
              {selected.notes && <p className="admin-copyright-notes">{selected.notes}</p>}

              <label>
                Admin notes
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} />
              </label>

              <div className="admin-copyright-actions">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    patch(
                      selected.id,
                      { status: 'in_progress', adminNotes: notes },
                      'Marked in progress',
                    )
                  }
                >
                  In progress
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    patch(selected.id, { status: 'rejected', adminNotes: notes }, 'Rejected')
                  }
                >
                  Reject
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    patch(selected.id, { status: 'resolved', adminNotes: notes }, 'Resolved')
                  }
                >
                  Resolve (no feed change)
                </button>
              </div>

              <h3>Matching feeds</h3>
              {candidates.length === 0 && (
                <p>No automatic host match. Open Config and search the URL, then resolve here.</p>
              )}
              <ul>
                {candidates.map((feed) => (
                  <li key={feed.id}>
                    <span>
                      {feed.name || feed.url} {feed.enabled === false ? '(already disabled)' : ''}
                    </span>
                    <button
                      type="button"
                      disabled={busy || feed.enabled === false}
                      onClick={() => {
                        if (
                          window.confirm(
                            `Disable RSS feed “${feed.name || feed.url}”? Harvest will stop for this source.`,
                          )
                        ) {
                          disableAndResolve(selected, feed);
                        }
                      }}
                    >
                      Disable feed & resolve
                    </button>
                  </li>
                ))}
              </ul>
            </aside>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminCopyrightPage;
